import { Component, OnDestroy, OnInit } from '@angular/core';
import { getDownloadURL, ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { BrandingColorsFormComponent } from '../../../../shared/components/branding-colors-form/branding-colors-form.component';
import { HeaderCustomizationComponent } from '../../../../shared/components/header-customization/header-customization.component';
import { SectionHeaderComponent } from '../../../../shared/components/section-header/section-header.component';
import { BrandingColors } from '../../../../shared/models/branding.model';
import { FORM_SECTION_DESCRIPTIONS } from '../../const/form-help-text.const';
import { AppForm } from '../../models/app-form';
import { BrandingSectionFormErrorJSON } from './models/branding-section';
import { formBrandingSectionErrors } from './utils/form-errors';

@Component({
    selector: 'app-branding-section',
    imports: [
        ReactiveFormsModule,
        MatExpansionModule,
        MatInputModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatDividerModule,
        MatSlideToggleModule,
        MatIconModule,
        MatCardModule,
        BrandingColorsFormComponent,
        HeaderCustomizationComponent,
        SectionHeaderComponent,
    ],
    templateUrl: './branding-section-component.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class BrandingSectionComponent implements OnInit, OnDestroy {
    public brandingSectionErrors: BrandingSectionFormErrorJSON;
    public readonly sectionDescription = FORM_SECTION_DESCRIPTIONS.branding;
    public isUploadingLightHeader = false;
    public isUploadingDarkHeader = false;
    public isUploadingFavIcon = false;
    public isUploadingLightLogin = false;
    public isUploadingDarkLogin = false;
    public isAlternativeLogoDisabled = false;
    private unsubscribe$ = new Subject<void>();

    constructor(
        private parent: FormGroupDirective,
        private storage: Storage,
        private snackBar: MatSnackBar
    ) {
        this.brandingSectionErrors = formBrandingSectionErrors;
    }

    ngOnInit(): void {
        this.setupLogoToggleListeners();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get parentForm(): FormGroup<AppForm> {
        return this.parent?.form;
    }

    get favIconURLControl(): FormControl<string | null> {
        return this.parentForm?.controls?.favIconURL;
    }

    get mainLogoLightUrlControl(): FormControl<string | null> {
        return this.parentForm?.controls?.mainLogoLightUrl;
    }

    get mainLogoDarkUrlControl(): FormControl<string | null> {
        return this.parentForm?.controls?.mainLogoDarkUrl;
    }

    get mainLogoWidthControl(): FormControl<string | null> {
        return this.parentForm?.controls?.mainLogoWidth;
    }

    get mainLogoHeightControl(): FormControl<string | null> {
        return this.parentForm?.controls?.mainLogoHeight;
    }

    get setMainLogoToHeaderControl(): FormControl<boolean | null> {
        return this.parentForm?.controls?.setMainLogoToHeader;
    }

    get setMainLogoToLoginControl(): FormControl<boolean | null> {
        return this.parentForm?.controls?.setMainLogoToLogin;
    }

    get alternativeLogoLightUrlControl(): FormControl<string | null> {
        return this.parentForm?.controls?.alternativeLogoLightUrl;
    }

    get alternativeLogoDarkUrlControl(): FormControl<string | null> {
        return this.parentForm?.controls?.alternativeLogoDarkUrl;
    }

    get alternativeLogoWidthControl(): FormControl<string | null> {
        return this.parentForm?.controls?.alternativeLogoWidth;
    }

    get alternativeLogoHeightControl(): FormControl<string | null> {
        return this.parentForm?.controls?.alternativeLogoHeight;
    }

    get setAlternativeLogoToHeaderControl(): FormControl<boolean | null> {
        return this.parentForm?.controls?.setAlternativeLogoToHeader;
    }

    get setAlternativeLogoToLoginControl(): FormControl<boolean | null> {
        return this.parentForm?.controls?.setAlternativeLogoToLogin;
    }

    get brandingColorsControl(): FormControl<BrandingColors> {
        return this.parentForm?.controls?.brandingColors;
    }

    onFileSelected(event: Event, type: 'light' | 'dark' | 'favicon'): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        let storagePath = `logos/${file.name}`;
        let control = '';
        const isLogin = input.id.includes('login');

        if (type === 'light') {
            if (isLogin) {
                this.isUploadingLightLogin = true;
                control = 'mainLogoLightUrl';
            } else {
                this.isUploadingLightHeader = true;
                control = 'alternativeLogoLightUrl';
            }
        } else if (type === 'dark') {
            if (isLogin) {
                this.isUploadingDarkLogin = true;
                control = 'mainLogoDarkUrl';
            } else {
                this.isUploadingDarkHeader = true;
                control = 'alternativeLogoDarkUrl';
            }
        } else if (type === 'favicon') {
            this.isUploadingFavIcon = true;
            control = 'favIconURL';
            storagePath = `favicons/${file.name}`;
        }

        const fileRef = ref(this.storage, storagePath);
        const uploadTask = uploadBytesResumable(fileRef, file);
        uploadTask
            .then(() =>
                getDownloadURL(fileRef).then((url) => {
                    this.parentForm.get(control)?.setValue(url);
                    if (type === 'light') {
                        if (isLogin) this.isUploadingLightLogin = false;
                        else {
                            this.isUploadingLightHeader = false;
                        }
                    } else if (type === 'dark') {
                        if (isLogin) this.isUploadingDarkLogin = false;
                        else {
                            this.isUploadingDarkHeader = false;
                        }
                    } else if (type === 'favicon') {
                        this.isUploadingFavIcon = false;
                    }
                })
            )
            .catch(() => {
                this.snackBar.open('File upload failed', 'Close', { duration: 3000 });
            });
    }

    setupLogoToggleListeners(): void {
        this.setMainLogoToHeaderControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            if (value) {
                this.setAlternativeLogoToHeaderControl.setValue(false, { emitEvent: false });
            } else {
                this.setAlternativeLogoToHeaderControl.setValue(true, { emitEvent: false });
            }
            this.updateAlternativeLogoState();
        });

        this.setAlternativeLogoToHeaderControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            if (value) {
                this.setMainLogoToHeaderControl.setValue(false, { emitEvent: false });
            } else {
                this.setMainLogoToHeaderControl.setValue(true, { emitEvent: false });
            }
            this.updateAlternativeLogoState();
        });

        this.setMainLogoToLoginControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            if (value) {
                this.setAlternativeLogoToLoginControl.setValue(false, { emitEvent: false });
            } else {
                this.setAlternativeLogoToLoginControl.setValue(true, { emitEvent: false });
            }
            this.updateAlternativeLogoState();
        });

        this.setAlternativeLogoToLoginControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            if (value) {
                this.setMainLogoToLoginControl.setValue(false, { emitEvent: false });
            } else {
                this.setMainLogoToLoginControl.setValue(true, { emitEvent: false });
            }
            this.updateAlternativeLogoState();
        });

        this.updateAlternativeLogoState();
    }

    updateAlternativeLogoState(): void {
        const mainHeader = this.setMainLogoToHeaderControl.value;
        const mainLogin = this.setMainLogoToLoginControl.value;

        this.isAlternativeLogoDisabled = !!mainHeader && !!mainLogin;

        if (this.isAlternativeLogoDisabled) {
            this.alternativeLogoLightUrlControl.disable({ emitEvent: false });
            this.alternativeLogoDarkUrlControl.disable({ emitEvent: false });
            this.alternativeLogoWidthControl.disable({ emitEvent: false });
            this.alternativeLogoHeightControl.disable({ emitEvent: false });
            this.setAlternativeLogoToHeaderControl.disable({ emitEvent: false });
            this.setAlternativeLogoToLoginControl.disable({ emitEvent: false });

            this.setAlternativeLogoToHeaderControl.setValue(false, { emitEvent: false });
            this.setAlternativeLogoToLoginControl.setValue(false, { emitEvent: false });
        } else {
            this.alternativeLogoLightUrlControl.enable({ emitEvent: false });
            this.alternativeLogoDarkUrlControl.enable({ emitEvent: false });
            this.alternativeLogoWidthControl.enable({ emitEvent: false });
            this.alternativeLogoHeightControl.enable({ emitEvent: false });
            this.setAlternativeLogoToHeaderControl.enable({ emitEvent: false });
            this.setAlternativeLogoToLoginControl.enable({ emitEvent: false });
        }
    }

    public updateBrandingData(_organizationData: unknown): void {
        this.updateAlternativeLogoState();
    }
}
